import type { BindingType } from '../shared/types/binding-types';
import type { Fill } from '../shared/utils/fill';
import type { LayerId, LayerKind, PieceModel } from './types';
import { findLayerByPieceId } from './nested-layers';

const HEX_COLOR = /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

type ContentPatch = { text: string } | { textFill: string } | { fill: Fill };

// Maps a binding value to a content patch, or returns null if it is handled elsewhere.
const bindingPatch = (bindingType: BindingType, value: unknown, kind: LayerKind): ContentPatch | null => {
  // Ignore empty values so existing content is preserved.
  if (value == null) return null;

  if (bindingType === 'text') return { text: String(value) };

  if (bindingType === 'fill-color') {
    const color = value === '' ? '#FFFFFF' : typeof value === 'string' && HEX_COLOR.test(value) ? value : null;
    if (!color) return null;

    // For pills and sobreimpresos, the fill color affects the text instead of the background.
    if (kind === 'pill' || kind === 'sobreimpreso') return { textFill: color };
    if (kind === 'text' || kind === 'shape') return { fill: { kind: 'solid', color } };
  }
  return null;
};

// 'unhandled': handled elsewhere, 'skip': no changes, otherwise apply the patch.
export type BindingResolution = { id: LayerId; patch: ContentPatch } | 'skip' | 'unhandled';

/** Resolves a form binding into a model update without applying it. */
export const resolveBinding = (
  model: PieceModel,
  pieceId: string,
  bindingType: BindingType,
  value: unknown
): BindingResolution => {
  const layer = findLayerByPieceId(model, pieceId);
  if (!layer || layer.kind === 'audio') return 'unhandled';

  // Only top-level layers can be patched directly.
  // Nested layers are rebuilt by their parent and handled elsewhere.
  if (model.layers[layer.id] !== layer) return 'unhandled';

  const patch = bindingPatch(bindingType, value, layer.kind);
  if (!patch) return 'unhandled';

  // Skip updates that would not change the layer.
  if (JSON.stringify({ ...layer.content, ...patch }) === JSON.stringify(layer.content)) return 'skip';
  return { id: layer.id, patch };
};
